import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, X, QrCode, Send, Save, ArrowUpDown, GripVertical } from "lucide-react";
import api, { formatApiErrorDetail, API_BASE } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import VideoInsertButton from "@/components/VideoInsertButton";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import QrShareDialog from "@/components/QrShareDialog";
import WhatsAppShareDialog from "@/components/WhatsAppShareDialog";

const empty = {
  brand: "", model: "", year: new Date().getFullYear(), price: 0, mileage: 0,
  fuel_type: "essence", transmission: "automatique", power_hp: 0, color: "", body_type: "",
  description: { fr: "", nl: "", en: "" }, features: [], images: [],
  history: "", warranty_months: 0, financing_available: true, delivery_europe: true,
  is_featured: false, status: "available", category: "modern", video_url: "", videos: [], price_confidential: false, sort_order: 0,
};

export default function AdminVehicles() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [featuresText, setFeaturesText] = useState("");
  const [imagesText, setImagesText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [uploadingVideo, setUploadingVideo] = useState(false);

  const onUploadVideo = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    setUploadingVideo(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${API_BASE}/files/${data.id}`;
      set("video_url", url);
      // Auto-save si véhicule déjà existant (édition) — évite l'oubli du bouton Save
      if (form.id) {
        try {
          await api.put(`/vehicles/${form.id}`, {
            ...form,
            year: Number(form.year), price: Number(form.price), mileage: Number(form.mileage),
            power_hp: Number(form.power_hp), warranty_months: Number(form.warranty_months),
            features: featuresText.split("\n").map((s) => s.trim()).filter(Boolean),
            images: imagesText.split("\n").map((s) => s.trim()).filter(Boolean),
            video_url: url,
          });
          toast.success("Vidéo uploadée et enregistrée");
          load();
        } catch (err) {
          toast.error("Vidéo uploadée mais échec enregistrement — cliquez Save");
        }
      } else {
        toast.success("Vidéo uploadée — pensez à Save pour créer le véhicule");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload vidéo échoué");
    } finally {
      setUploadingVideo(false);
      e.target.value = "";
    }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });
    const CONCURRENCY = 5;
    const results = new Array(files.length);
    const errors = [];
    let done = 0;
    let cursor = 0;
    const uploadOne = async (idx, f) => {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        results[idx] = `${API_BASE}/files/${data.id}`;
      } catch (err) {
        errors.push(`${f.name}: ${formatApiErrorDetail(err.response?.data?.detail) || err.message || "upload échoué"}`);
      } finally {
        done += 1;
        setUploadProgress({ done, total: files.length });
      }
    };
    const worker = async () => {
      while (cursor < files.length) {
        const i = cursor++;
        await uploadOne(i, files[i]);
      }
    };
    try {
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, files.length) }, worker));
      const uploaded = results.filter(Boolean);
      setImagesText((prev) => [prev.trim(), ...uploaded].filter(Boolean).join("\n"));
      if (uploaded.length) toast.success(`${uploaded.length}/${files.length} photo${uploaded.length > 1 ? "s" : ""} ajoutée${uploaded.length > 1 ? "s" : ""}`);
      if (errors.length) toast.error(`${errors.length} échec${errors.length > 1 ? "s" : ""} — ${errors[0]}`);
    } finally {
      setUploading(false);
      setUploadProgress({ done: 0, total: 0 });
      e.target.value = "";
    }
  };

  const removeImage = (idx) => {
    const arr = imagesText.split("\n").map((s) => s.trim()).filter(Boolean);
    arr.splice(idx, 1);
    setImagesText(arr.join("\n"));
  };
  const moveImage = (idx, dir) => {
    const arr = imagesText.split("\n").map((s) => s.trim()).filter(Boolean);
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setImagesText(arr.join("\n"));
  };

  const load = () => api.get("/vehicles", { params: { limit: 500 } }).then((r) => {
    setItems(r.data);
    // Snapshot du sort_order courant pour détecter les modifs non enregistrées
    const map = {};
    r.data.forEach((v) => { map[v.id] = v.sort_order || 0; });
    setOrderDraft(map);
  });
  useEffect(() => { load(); }, []);

  // Ordre d'affichage manuel — brouillon côté client
  const [orderDraft, setOrderDraft] = useState({});
  const [savingOrder, setSavingOrder] = useState(false);
  const orderDirty = items.some((v) => (orderDraft[v.id] ?? 0) !== (v.sort_order || 0));

  const setOrderFor = (id, val) => {
    const num = val === "" ? 0 : parseInt(val, 10);
    setOrderDraft((s) => ({ ...s, [id]: isNaN(num) ? 0 : num }));
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      const payload = items
        .filter((v) => (orderDraft[v.id] ?? 0) !== (v.sort_order || 0))
        .map((v) => ({ id: v.id, sort_order: orderDraft[v.id] ?? 0 }));
      if (!payload.length) { toast.info("Aucun changement à enregistrer"); return; }
      const { data } = await api.post("/vehicles/bulk-sort", payload);
      toast.success(`Ordre enregistré (${data.updated} véhicule${data.updated > 1 ? "s" : ""})`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur enregistrement ordre");
    } finally {
      setSavingOrder(false);
    }
  };

  // Drag & drop réordonnancement
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const onDragStart = (id) => (e) => {
    setDragId(id);
    try { e.dataTransfer.effectAllowed = "move"; } catch {}
  };
  const onDragOver = (id) => (e) => {
    e.preventDefault();
    try { e.dataTransfer.dropEffect = "move"; } catch {}
    if (id !== dragOverId) setDragOverId(id);
  };
  const onDragLeave = () => setDragOverId(null);
  const onDragEnd = () => { setDragId(null); setDragOverId(null); };

  const onDrop = (targetId) => (e) => {
    e.preventDefault();
    const source = dragId;
    setDragId(null); setDragOverId(null);
    if (!source || source === targetId) return;
    const srcItem = items.find((v) => v.id === source);
    const dstItem = items.find((v) => v.id === targetId);
    if (!srcItem || !dstItem) return;
    // Interdit le drop entre sections (statuts) différents
    if (srcItem.status !== dstItem.status) {
      toast.error("Impossible de déplacer entre sections différentes");
      return;
    }
    const srcIdx = items.findIndex((v) => v.id === source);
    const dstIdx = items.findIndex((v) => v.id === targetId);
    const next = [...items];
    const [moved] = next.splice(srcIdx, 1);
    next.splice(dstIdx, 0, moved);
    setItems(next);
    // Réassigne sort_order UNIQUEMENT au sein de la section (statut) du véhicule déplacé
    const section = srcItem.status;
    const draft = { ...orderDraft };
    let counter = 0;
    next.forEach((v) => {
      if (v.status === section) {
        counter += 10;
        draft[v.id] = counter;
      }
    });
    setOrderDraft(draft);
  };

  // Auto-open edit dialog if navigated with ?edit={vehicle_id}
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId || !items.length) return;
    const v = items.find((x) => x.id === editId);
    if (v) { openEdit(v); setSearchParams({}, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, searchParams]);

  const openNew = () => { setForm(empty); setFeaturesText(""); setImagesText(""); setOpen(true); };
  const openEdit = (v) => {
    setForm({ ...empty, ...v, description: { fr: "", nl: "", en: "", ...(v.description || {}) } });
    setFeaturesText((v.features || []).join("\n"));
    setImagesText((v.images || []).join("\n"));
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      year: Number(form.year), price: Number(form.price), mileage: Number(form.mileage),
      power_hp: Number(form.power_hp), warranty_months: Number(form.warranty_months),
      features: featuresText.split("\n").map((s) => s.trim()).filter(Boolean),
      images: imagesText.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (form.id) await api.put(`/vehicles/${form.id}`, payload);
      else await api.post("/vehicles", payload);
      toast.success(form.id ? "Véhicule mis à jour" : "Véhicule ajouté");
      setOpen(false); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm(t.admin.confirm_delete)) return;
    await api.delete(`/vehicles/${id}`); toast.success("Supprimé"); load();
  };

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const [qrFor, setQrFor] = useState(null);
  const [waFor, setWaFor] = useState(null);
  const shareOrigin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <div data-testid="admin-vehicles">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <h1 className="font-display text-4xl tracking-tight font-light text-white">Véhicules</h1>
        <div className="flex items-center gap-3">
          <Button
            data-testid="save-order"
            onClick={saveOrder}
            disabled={!orderDirty || savingOrder}
            className="bg-black border border-[#C8A75A]/40 text-[#C8A75A] hover:bg-[#C8A75A] hover:text-black uppercase text-xs tracking-widest disabled:opacity-40"
          >
            <Save className="w-4 h-4 mr-2" />
            {savingOrder ? "Enregistrement…" : orderDirty ? "Enregistrer l'ordre" : "Ordre à jour"}
          </Button>
          <Button data-testid="new-vehicle" onClick={openNew} className="bg-[#C8A75A] text-black hover:bg-white uppercase text-xs tracking-widest">
            <Plus className="w-4 h-4 mr-2" /> {t.admin.new}
          </Button>
        </div>
      </div>

      <div className="space-y-8">
        {[
          { key: "available", label: "À vendre", subtitle: "Véhicules disponibles au catalogue — priorité d'affichage", tint: "text-emerald-400", dot: "bg-emerald-400", ring: "border-emerald-500/25" },
          { key: "reserved", label: "Réservées", subtitle: "Sous option — badge visible sur la fiche", tint: "text-amber-400", dot: "bg-amber-400", ring: "border-amber-500/25" },
          { key: "sold", label: "Trop tard, déjà sur nos routes", subtitle: "Vendues — affichées en bas et sur la section « Sur nos routes »", tint: "text-white/70", dot: "bg-white/50", ring: "border-white/15" },
        ].map((section) => {
          const rows = items.filter((v) => v.status === section.key);
          return (
            <div key={section.key} data-testid={`admin-section-${section.key}`} className={`bg-[#1E1E1E] border ${section.ring} rounded-md overflow-hidden`}>
              <div className="flex items-end justify-between gap-4 px-6 py-4 bg-black/40 border-b border-white/5">
                <div>
                  <div className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] ${section.tint} mb-1`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${section.dot}`} />
                    {section.label}
                  </div>
                  <p className="text-white/40 text-xs">{section.subtitle}</p>
                </div>
                <span className="text-white/40 text-xs uppercase tracking-widest">{rows.length} véh.</span>
              </div>
              {rows.length === 0 ? (
                <div className="p-8 text-center text-white/40 text-sm">Aucun véhicule dans cette section</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[1050px]">
                    <thead className="text-white/50 text-xs uppercase tracking-widest">
                      <tr>
                        <th className="w-8 px-2 py-3"></th>
                        <th className="text-left px-4 py-3" title="Ordre au sein de la section (croissant)">
                          <span className="inline-flex items-center gap-1"><ArrowUpDown className="w-3 h-3" /> Ordre</span>
                        </th>
                        <th className="text-left px-6 py-3">Véhicule</th>
                        <th className="text-left px-6 py-3">Année</th>
                        <th className="text-left px-6 py-3">Prix</th>
                        <th className="text-left px-6 py-3">KM</th>
                        <th className="text-left px-6 py-3">Vedette</th>
                        <th className="text-right px-6 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((v) => {
                        const draft = orderDraft[v.id] ?? 0;
                        const dirty = draft !== (v.sort_order || 0);
                        const isDragging = dragId === v.id;
                        const isDropTarget = dragOverId === v.id && dragId && dragId !== v.id;
                        return (
                          <tr
                            key={v.id}
                            data-testid={`vehicle-row-${v.id}`}
                            draggable
                            onDragStart={onDragStart(v.id)}
                            onDragOver={onDragOver(v.id)}
                            onDragLeave={onDragLeave}
                            onDrop={onDrop(v.id)}
                            onDragEnd={onDragEnd}
                            className={`border-t border-white/5 transition-colors ${isDragging ? "opacity-40" : ""} ${isDropTarget ? "bg-[#C8A75A]/10 border-t-2 border-t-[#C8A75A]" : ""}`}
                          >
                            <td className="px-2 py-4 text-center cursor-grab active:cursor-grabbing" title="Glissez pour réordonner dans cette section">
                              <GripVertical className="w-4 h-4 text-white/30 hover:text-[#C8A75A] mx-auto" data-testid={`drag-handle-${v.id}`} />
                            </td>
                            <td className="px-4 py-4">
                              <input
                                type="number"
                                data-testid={`sort-order-${v.id}`}
                                value={draft}
                                onChange={(e) => setOrderFor(v.id, e.target.value)}
                                className={`w-20 bg-[#0D0D0D] border rounded-md px-3 py-2 text-sm text-white text-center outline-none transition-colors ${dirty ? "border-[#C8A75A]/60" : "border-white/10 focus:border-white/30"}`}
                              />
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-white">{v.brand} {v.model}</p>
                              <p className="text-white/40 text-xs">{v.color}</p>
                            </td>
                            <td className="px-6 py-4 text-white/70">{v.year}</td>
                            <td className="px-6 py-4 text-white/70">{v.price ? `${new Intl.NumberFormat("fr-BE").format(v.price)} €` : "-"}</td>
                            <td className="px-6 py-4 text-white/70">{new Intl.NumberFormat("fr-BE").format(v.mileage)}</td>
                            <td className="px-6 py-4">{v.is_featured && <Star className="w-4 h-4 text-[#C8A75A]" fill="#C8A75A" />}</td>
                            <td className="px-6 py-4 text-right whitespace-nowrap">
                              <button onClick={() => setWaFor(v)} data-testid={`wa-${v.id}`} title="Envoyer sur WhatsApp (carte + photos + message)" className="text-[#25D366] hover:text-white p-2"><Send className="w-4 h-4" /></button>
                              <button onClick={() => setQrFor(v)} data-testid={`qr-${v.id}`} title="Partager (QR + WhatsApp)" className="text-[#C8A75A] hover:text-white p-2"><QrCode className="w-4 h-4" /></button>
                              <button onClick={() => openEdit(v)} data-testid={`edit-${v.id}`} className="text-white/60 hover:text-[#C8A75A] p-2"><Pencil className="w-4 h-4" /></button>
                              <button onClick={() => del(v.id)} data-testid={`del-${v.id}`} className="text-white/60 hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-white/40 text-xs mt-3 leading-relaxed">
        Astuce : le drag & drop réordonne <strong className="text-white/70">au sein d'une section</strong> uniquement (À vendre / Réservées / Trop tard). Chaque section a ses propres numéros d'ordre, indépendants les uns des autres. Cliquez "<strong className="text-[#C8A75A]">Enregistrer l'ordre</strong>" pour appliquer d'un coup toutes les modifications.
      </p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#1E1E1E] border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto">          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{form.id ? "Modifier" : "Nouveau véhicule"}</DialogTitle>
            <DialogDescription className="text-white/50 text-xs">Complétez les infos du véhicule (multi-langues, photos, vidéo).</DialogDescription>
          </DialogHeader>

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <F label="Marque"><Input data-testid="f-brand" value={form.brand} onChange={(e) => set("brand", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></F>
            <F label="Modèle"><Input data-testid="f-model" value={form.model} onChange={(e) => set("model", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></F>
            <F label="Année"><Input type="number" value={form.year} onChange={(e) => set("year", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></F>
            <F label="Prix (€)"><Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></F>
            <F label="Km"><Input type="number" value={form.mileage} onChange={(e) => set("mileage", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></F>
            <F label="Puissance (ch)"><Input type="number" value={form.power_hp} onChange={(e) => set("power_hp", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></F>
            <F label="Carburant">
              <Select value={form.fuel_type} onValueChange={(v) => set("fuel_type", v)}>
                <SelectTrigger className="bg-[#0D0D0D] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1E1E1E] border-white/10 text-white">
                  {["essence","diesel","hybride","electrique"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Transmission">
              <Select value={form.transmission} onValueChange={(v) => set("transmission", v)}>
                <SelectTrigger className="bg-[#0D0D0D] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1E1E1E] border-white/10 text-white">
                  {["automatique","manuelle"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Couleur"><Input value={form.color} onChange={(e) => set("color", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></F>
            <F label="Carrosserie"><Input value={form.body_type} onChange={(e) => set("body_type", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></F>
            <F label="Garantie (mois)"><Input type="number" value={form.warranty_months} onChange={(e) => set("warranty_months", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></F>
            <F label="Statut">
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="bg-[#0D0D0D] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1E1E1E] border-white/10 text-white">
                  {["available","reserved","sold"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Rubrique">
              <Select value={form.category || "modern"} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="f-category" className="bg-[#0D0D0D] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1E1E1E] border-white/10 text-white">
                  <SelectItem value="modern">Modern (par défaut)</SelectItem>
                  <SelectItem value="youngtimer">Youngtimer (15–30 ans)</SelectItem>
                  <SelectItem value="oldtimer">Oldtimer (+30 ans)</SelectItem>
                </SelectContent>
              </Select>
            </F>
          </div>

          <VehicleDescriptionFields form={form} set={set} />

          <F label="Historique"><Textarea rows={3} value={form.history} onChange={(e) => set("history", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></F>

          <div className="mt-6 bg-[#0D0D0D]/50 border border-[#C8A75A]/25 rounded-md p-5">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <label htmlFor="vehicle-video-url" className="text-xs uppercase tracking-[0.25em] text-[#C8A75A] font-semibold">
                Lien Vidéo
              </label>
              <label className="cursor-pointer text-[10px] uppercase tracking-widest text-[#C8A75A] hover:bg-[#C8A75A] hover:text-black flex items-center gap-2 border border-[#C8A75A]/40 rounded-md px-3 py-2 transition-colors">
                <input
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm"
                  onChange={onUploadVideo}
                  disabled={uploadingVideo}
                  className="hidden"
                  data-testid="vehicle-video-upload"
                />
                {uploadingVideo ? "Upload…" : "Téléverser un MP4"}
              </label>
            </div>
            <Input
              id="vehicle-video-url"
              data-testid="vehicle-video-url"
              value={form.video_url}
              onChange={(e) => set("video_url", e.target.value)}
              className="bg-[#0D0D0D] border-white/15 focus:border-[#C8A75A]/60 h-11 font-mono text-sm"
              placeholder="Collez ici un lien YouTube, Vimeo ou MP4… ou téléversez"
            />
            <p className="text-white/50 text-[11px] mt-2 leading-relaxed">
              Ce lien est utilisé pour afficher le lecteur en tête de la fiche véhicule. Après téléversement d'un MP4, l'URL s'inscrit automatiquement ici. YouTube et Vimeo sont détectés et lus en iframe responsive.
            </p>
            {form.video_url && (
              <div className="mt-3 flex items-center gap-2 text-xs">
                <span className="text-emerald-400">● Vidéo liée</span>
                <a href={form.video_url} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#C8A75A] truncate max-w-[400px]">
                  {form.video_url}
                </a>
                <button type="button" onClick={() => set("video_url", "")} className="ml-auto text-white/40 hover:text-red-400 text-[10px] uppercase tracking-widest">Retirer</button>
              </div>
            )}
          </div>

          {/* Vidéos supplémentaires — onglets sur la fiche publique */}
          <div className="mt-4 bg-[#0D0D0D]/50 border border-white/10 rounded-md p-5">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <label className="text-xs uppercase tracking-[0.25em] text-[#C8A75A] font-semibold">Vidéos supplémentaires (onglets)</label>
              <button
                type="button"
                data-testid="add-extra-video"
                onClick={() => set("videos", [...(form.videos || []), { url: "", label: "" }])}
                className="text-[10px] uppercase tracking-widest text-[#C8A75A] hover:bg-[#C8A75A] hover:text-black border border-[#C8A75A]/40 rounded-md px-3 py-2 transition-colors"
              >
                + Ajouter une vidéo
              </button>
            </div>
            {(form.videos || []).length === 0 ? (
              <p className="text-white/40 text-xs">Aucune vidéo supplémentaire. Ajoutez-en pour créer des onglets (ex : Démarrage moteur, Tour extérieur, Intérieur).</p>
            ) : (
              <div className="space-y-2">
                {(form.videos || []).map((entry, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-center">
                    <Input
                      data-testid={`extra-video-label-${idx}`}
                      value={entry.label || ""}
                      onChange={(e) => {
                        const next = [...(form.videos || [])];
                        next[idx] = { ...next[idx], label: e.target.value };
                        set("videos", next);
                      }}
                      placeholder="Libellé (ex : Démarrage)"
                      className="bg-[#0D0D0D] border-white/10 h-10 text-sm"
                    />
                    <Input
                      data-testid={`extra-video-url-${idx}`}
                      value={entry.url || ""}
                      onChange={(e) => {
                        const next = [...(form.videos || [])];
                        next[idx] = { ...next[idx], url: e.target.value };
                        set("videos", next);
                      }}
                      placeholder="URL YouTube, Vimeo ou MP4"
                      className="bg-[#0D0D0D] border-white/10 h-10 font-mono text-xs"
                    />
                    <button
                      type="button"
                      data-testid={`extra-video-remove-${idx}`}
                      onClick={() => {
                        const next = [...(form.videos || [])];
                        next.splice(idx, 1);
                        set("videos", next);
                      }}
                      className="text-white/40 hover:text-red-400 p-2"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-white/40 text-[11px] mt-3 leading-relaxed">
              La vidéo principale ci-dessus reste toujours le 1er onglet. Ajoutez ici des vidéos secondaires pour créer des onglets de navigation sur la fiche publique.
            </p>
          </div>

          <F label="Équipements (1 par ligne)"><Textarea rows={4} value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} className="bg-[#0D0D0D] border-white/10" /></F>

          <div className="mt-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-[0.25em] text-white/50">Photos du véhicule ({imagesText.split("\n").filter((s) => s.trim()).length})</label>
              <label className="cursor-pointer text-xs uppercase tracking-widest text-[#C8A75A] hover:text-white flex items-center gap-2 border border-[#C8A75A]/40 rounded-md px-3 py-1.5">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onUpload}
                  disabled={uploading}
                  className="hidden"
                  data-testid="vehicle-image-upload"
                />
                {uploading
                  ? `Upload ${uploadProgress.done}/${uploadProgress.total}…`
                  : "+ Ajouter photos"}
              </label>
            </div>

            {/* Thumbnails grid — visual management, drag-free reorder via arrows */}
            {imagesText.split("\n").map((s) => s.trim()).filter(Boolean).length > 0 && (
              <div data-testid="vehicle-images-grid" className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mb-3 p-3 bg-[#0D0D0D] border border-white/10 rounded-md max-h-[360px] overflow-y-auto">
                {imagesText.split("\n").map((s) => s.trim()).filter(Boolean).map((url, idx, arr) => (
                  <div key={`${url}-${idx}`} className="group relative aspect-square bg-black/40 rounded-md overflow-hidden">
                    <img src={url} alt={`photo ${idx + 1}`} className="w-full h-full object-cover" loading="lazy" />
                    <span className="absolute top-1 left-1 text-[9px] px-1.5 py-0.5 rounded bg-black/70 text-white/80">{idx + 1}</span>
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 flex items-center justify-center gap-1">
                      <button type="button" title="Reculer" disabled={idx === 0} onClick={() => moveImage(idx, -1)} className="w-7 h-7 rounded bg-[#1E1E1E] text-white disabled:opacity-30 hover:bg-[#C8A75A] hover:text-black text-xs">←</button>
                      <button type="button" title="Avancer" disabled={idx === arr.length - 1} onClick={() => moveImage(idx, 1)} className="w-7 h-7 rounded bg-[#1E1E1E] text-white disabled:opacity-30 hover:bg-[#C8A75A] hover:text-black text-xs">→</button>
                      <button type="button" title="Supprimer" onClick={() => removeImage(idx)} className="w-7 h-7 rounded bg-red-500/80 text-white hover:bg-red-500 text-xs" data-testid={`remove-image-${idx}`}>×</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <details className="mb-1">
              <summary className="text-[10px] uppercase tracking-[0.25em] text-white/40 cursor-pointer hover:text-white/70">Voir / éditer les URLs (avancé)</summary>
              <Textarea rows={4} value={imagesText} onChange={(e) => setImagesText(e.target.value)} className="bg-[#0D0D0D] border-white/10 mt-2 text-xs" placeholder="https://..." />
            </details>
            <p className="text-white/40 text-[10px] mt-2">JPG, PNG, WEBP. Max 30 Mo par photo. Multi-sélection (jusqu'à ~30 photos par véhicule). La 1<sup>ère</sup> photo est la vignette de la fiche.</p>
          </div>

          <div className="flex flex-wrap items-center gap-6 mt-4">
            <SwitchRow label="En vedette" checked={form.is_featured} onChange={(v) => set("is_featured", v)} testid="sw-featured" />
            <SwitchRow label="Financement" checked={form.financing_available} onChange={(v) => set("financing_available", v)} />
            <SwitchRow label="Livraison Europe" checked={form.delivery_europe} onChange={(v) => set("delivery_europe", v)} />
            <SwitchRow label="Prix confidentiel (pièce rare)" checked={form.price_confidential} onChange={(v) => set("price_confidential", v)} testid="sw-price-confidential" />
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-white/10">
            <Button variant="outline" onClick={() => setOpen(false)} className="border-white/20 text-white hover:bg-white/5">{t.admin.cancel}</Button>
            <Button data-testid="save-vehicle" onClick={save} className="bg-[#C8A75A] text-black hover:bg-white uppercase text-xs tracking-widest">{t.admin.save}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {qrFor && (
        <QrShareDialog
          open={!!qrFor}
          onClose={() => setQrFor(null)}
          title={`${qrFor.brand} · ${qrFor.year}`}
          subtitle={`${qrFor.model} · ${qrFor.price ? new Intl.NumberFormat("fr-BE").format(qrFor.price) + " €" : "Prix sur demande"}`}
          url={`${shareOrigin}/stock/${qrFor.id}`}
          waMessage={`${qrFor.brand} ${qrFor.model} · ${qrFor.year} · ${new Intl.NumberFormat("fr-BE").format(qrFor.mileage || 0)} km · ${qrFor.price ? new Intl.NumberFormat("fr-BE").format(qrFor.price) + " €" : "Prix sur demande"}\nKV International — Rhode-Saint-Genèse`}
          downloadName={`kv-vehicle-${qrFor.id}`}
        />
      )}
      <WhatsAppShareDialog open={!!waFor} onClose={() => setWaFor(null)} vehicle={waFor} />
    </div>
  );
}

function F({ label, children }) {
  return (
    <div className="mt-3">
      <label className="text-[10px] uppercase tracking-[0.25em] text-white/50 block mb-2">{label}</label>
      {children}
    </div>
  );
}

function VehicleDescriptionFields({ form, set }) {
  const refFr = useRef(null);
  const refNl = useRef(null);
  const refEn = useRef(null);
  const refs = { fr: refFr, nl: refNl, en: refEn };
  const setDesc = (lang, value) => set("description", { ...form.description, [lang]: value });

  return (
    <div className="grid md:grid-cols-3 gap-4 mt-4">
      {["fr", "nl", "en"].map((l) => (
        <div key={l} className="mt-3">
          <div className="flex items-center justify-between mb-2 gap-2">
            <label className="text-[10px] uppercase tracking-[0.25em] text-white/50">Description ({l.toUpperCase()})</label>
            <VideoInsertButton
              targetRef={refs[l]}
              value={form.description[l]}
              onChange={(v) => setDesc(l, v)}
              label={l.toUpperCase()}
              testid={`vehicle-desc-${l}`}
            />
          </div>
          <Textarea
            ref={refs[l]}
            rows={5}
            value={form.description[l]}
            onChange={(e) => setDesc(l, e.target.value)}
            placeholder={`Description en ${l.toUpperCase()}…\n\n[video]https://youtu.be/xyz[/video]\n\nSuite du texte…`}
            className="bg-[#0D0D0D] border-white/10 font-mono text-sm"
          />
        </div>
      ))}
    </div>
  );
}

function SwitchRow({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 text-sm text-white/70 cursor-pointer">
      <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
      {label}
    </label>
  );
}
